"use client";
import React, { useEffect, useRef, useState } from "react";
import {  Instagram, Linkedin } from "lucide-react";
import Link from "next/link";
import BlogService from "@/app/services/blog";
import { BASE_URL } from "@/app/contants/url";
import NewsSkeleton from "@/app/components/skeleton/NewsSkeleton";
import { useTranslations, useLocale } from 'next-intl';

const colors = ["bg-[#ffd0f6]", "bg-[#b7dfff]", "bg-[#cbb2ba]", "bg-[#c7e1f8]"]

export default function News() {
  const t = useTranslations('News');
  const locale = useLocale();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([])
  const [error, setError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)


  async function getData() {
    if (data?.length <= 0) { setIsLoading(true) }
    try {
      const body = {
        page: 1,
        page_size: 9,
        language: locale,
        sort: 'desc'
      }
      const result: any = await BlogService.getList(body);
      if (result?.data?.success) {
        setData(result?.data?.data)
        setError(false)
      } else {
        setError(true)
      }
    } catch (error: any) {
      setError(true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => { getData() }, [])

  return (
    <>
      {isLoading ? <NewsSkeleton /> : (
        <>
          {error ? <></> : (
            <>
              {data?.length > 0 && (
                <section className="bg-[#120428] py-20 px-6 md:px-16 text-white min-h-screen">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6">
                    <div>
                      <h2 className="text-6xl font-bold tracking-tight">{t('articles')}</h2>
                    </div>
                    <div className="flex gap-3">
                      <Link href='https://www.instagram.com/primalt.ai/' target="_blank" className="cursor-pointer"><SocialPill icon={<Instagram size={16} />} label="Instagram" active /></Link>
                      <Link href='https://www.linkedin.com/company/primalt/' target="_blank" className="cursor-pointer"><SocialPill icon={<Linkedin size={16} />} label="LinkedIn" /></Link>
                    </div>
                  </div>

                  <div
                    ref={carouselRef}
                    className="flex gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth pb-10"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  >
                    {data?.map((post: any, index: number) => (
                      <div
                        key={post.id}
                        className="flex-none w-[85%] md:w-[calc(33.333%-16px)] snap-start rounded-b-none"
                      >
                        <div className="rounded-[40px] rounded-b-none overflow-hidden h-[550px] flex flex-col shadow-2xl">
                          <div className="h-[75%] relative">
                            <img src={`${BASE_URL}storage/${post?.cover_path}`} alt={post.title} className="w-full h-full object-cover" />
                          </div>

                          {/* Content Section */}
                          <div className={`${colors[index % colors.length]} h-[35%] p-10 text-black flex flex-col justify-between rounded-b-none`}>
                            <p className="text-lg font-medium leading-snug">
                              {post?.title}
                            </p>
                            <Link href={`/blog/${post?.slug}`} className="text-sm font-bold underline hover:opacity-70 transition-opacity text-left cursor-pointer">
                              {t('read_article')}
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Navigation Controls */}
                  <div className="flex items-center justify-center mt-8 gap-6">
                    <Link href='/blog' className="underline">{t('view_all')}</Link>
                  </div>
                </section>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

function SocialPill({ icon, label, active = false }: { icon: any, label: string, active?: boolean }) {
  return (
    <button className={`flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium transition-all
      ${active ? "bg-[#7C4DFF] border-[#7C4DFF]" : "border-gray-800 hover:border-gray-600 bg-white/5"}`}>
      {label} {icon}
    </button>
  );
}